import { lazy, memo, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { MessageCircle, Phone, MapPin, Mail, Globe, Instagram, ArrowUp, ChevronLeft, ChevronRight } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { toast } from "@/hooks/use-toast";
import { useSiteContent } from "@/hooks/useSiteContent";
import type { CarRow } from "@/lib/site-content";
import { DEFAULT_RU_EN_DICT } from "@/lib/site-content";

const ChatConsultant = lazy(() =>
  import("@/components/ChatConsultant").then((m) => ({ default: m.ChatConsultant }))
);
import { SeoCardsMobileLoop } from "@/components/SeoCardsMobileLoop";
const LazyMap = lazy(() =>
  import("@/components/LazyMap").then((m) => ({ default: m.LazyMap }))
);
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useLang } from "@/i18n/LanguageProvider";
import { pickLang, pickText, pickTextWithDict, pickShortLabel, STRINGS } from "@/i18n/strings";
import { getI18n } from "@/i18n/site";
import logo from "@/assets/logo.png";
const heroCarFallback = "/hero-car.webp";
import aboutFallback from "@/assets/about.webp";
import aboutFallbackEn from "@/assets/about-en.webp";
import bannerFallback from "@/assets/banner.webp";
import processFallback from "@/assets/process.webp";
import carPlaceholder from "@/assets/hyundai.jpg";
import seoSedan from "@/assets/seo-sedan.png";
import seoSuv from "@/assets/seo-suv.png";
import seoAirport from "@/assets/seo-airport.png";
import seoPremium from "@/assets/seo-premium.png";
import seoTravel from "@/assets/seo-travel.png";

type CategoryKey = "econom" | "comfort" | "business" | "premium";

const Header = () => {
  const { lang } = useLang();
  const [show, setShow] = useState(true);
  useEffect(() => {
    let lastY = window.scrollY;
    let accumDown = 0;
    const onScroll = () => {
      const y = window.scrollY;
      const delta = y - lastY;
      if (y < 80) {
        setShow(true);
        accumDown = 0;
      } else if (delta > 0) {
        accumDown += delta;
        if (accumDown > 40) setShow(false);
      } else if (delta < 0) {
        setShow(true);
        accumDown = 0;
      }
      lastY = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header
      className={`fixed top-4 left-0 right-0 z-50 site-container transition-all duration-300 ${
        show ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-4 pointer-events-none"
      }`}
    >
      <div className="flex md:grid md:grid-cols-[auto_1fr_auto] items-center justify-center bg-card rounded-full px-4 md:px-6 py-2 gap-2">
        <a href="#" className="flex items-center">
          <img src={logo} alt="Freeways автопрокат" className="h-9 md:h-10 w-auto" />
        </a>
        <nav className="hidden md:flex items-center justify-center gap-2 lg:gap-6 text-sm">
          <a href="#" className="px-3 py-2 rounded-full hover:bg-secondary transition">{pickLang(STRINGS.nav.home, lang)}</a>
          <a href="#aboutus" className="px-3 py-2 rounded-full hover:bg-secondary transition">{pickLang(STRINGS.nav.about, lang)}</a>
          <a href="#catalog" className="px-3 py-2 rounded-full hover:bg-secondary transition">{pickLang(STRINGS.nav.catalog, lang)}</a>
          <a href="#contacts" className="px-3 py-2 rounded-full hover:bg-secondary transition">{pickLang(STRINGS.nav.contacts, lang)}</a>
        </nav>
        <div className="flex items-center justify-end ml-auto md:ml-0">
          <LanguageSwitcher />
        </div>
      </div>
    </header>
  );
};

const ScrollToTop = () => {
  const { lang } = useLang();
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <button
      type="button"
      aria-label={pickLang(STRINGS.scrollTop, lang)}
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      className={`fixed bottom-6 right-[212px] z-50 h-11 w-11 rounded-full bg-primary text-primary-foreground shadow-[0_8px_24px_hsl(var(--primary)/0.4)] flex items-center justify-center transition-opacity duration-300 hover:opacity-90 ${
        show ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
    >
      <ArrowUp size={20} />
    </button>
  );
};

const ConsultForm = ({
  title,
  description,
  bannerUrl,
  submitLabel,
  namePlaceholder,
  phonePlaceholder,
  buttonColor,
}: {
  title: string;
  description: string;
  bannerUrl: string | null;
  submitLabel: string;
  namePlaceholder: string;
  phonePlaceholder: string;
  buttonColor: string;
}) => {
  const { lang } = useLang();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  return (
    <div className="relative overflow-hidden rounded-[20px]">
      <img
        src={bannerUrl ?? bannerFallback}
        alt=""
        loading="lazy"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover"
      />

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!name || !phone) return;
          toast({
            title: pickLang(STRINGS.consult.formSubmittedTitle, lang),
            description: pickLang(STRINGS.consult.formSubmittedDesc, lang),
          });
          setName("");
          setPhone("");
        }}
        className="relative z-10 px-4 sm:px-6 py-8 sm:py-14 md:py-20 flex flex-col items-center text-center text-white"
      >
        <h3 className="text-2xl sm:text-3xl md:text-5xl font-medium tracking-tight text-white">{title}</h3>
        <p className="mt-3 sm:mt-4 text-sm sm:text-base md:text-lg text-white/90 max-w-xl">{description}</p>
        <div className="mt-8 w-full max-w-xl flex flex-col gap-3">
          <Input
            type="text"
            name="name"
            autoComplete="name"
            inputMode="text"
            placeholder={namePlaceholder}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="consult-input rounded-2xl h-12 border-0"
          />
          <Input
            type="tel"
            name="phone"
            autoComplete="tel"
            inputMode="tel"
            placeholder={phonePlaceholder}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="consult-input rounded-2xl h-12 border-0"
          />
          <Button
            type="submit"
            className="rounded-2xl h-12 px-8 font-semibold text-white border-0 hover:opacity-90"
            style={{
              backgroundColor: `hsl(${buttonColor})`,
              boxShadow: `0 4px 20px 0 hsl(${buttonColor} / 0.3), 0 -4px 13px 0 rgba(0,0,0,0.2), 1px 0 0 0 rgba(255,255,255,0.4)`,
            }}
          >
            {submitLabel}
          </Button>
        </div>
      </form>
    </div>
  );
};

const AUTOPLAY_DELAY = 4000; // ms между слайдами
const TRANSITION_DURATION = 30; // скорость перехода (меньше = быстрее)

const CarsCarousel = ({
  cars,
  onOpen,
}: {
  cars: CarRow[];
  onOpen: (car: CarRow) => void;
}) => {
  const { lang } = useLang();
  const autoplay = useMemo(
    () => Autoplay({ delay: AUTOPLAY_DELAY, stopOnInteraction: false, stopOnMouseEnter: true }),
    []
  );
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      align: "start",
      loop: true,
      slidesToScroll: 1,
      duration: TRANSITION_DURATION,
      dragThreshold: 12,
      watchDrag: true,
      skipSnaps: false,
      inViewThreshold: 0.6,
    },
    [autoplay]
  );

  const [currentSlide, setCurrentSlide] = useState(0);
  useEffect(() => {
    if (!emblaApi) return;
    const update = () => setCurrentSlide(emblaApi.selectedScrollSnap());
    update();
    emblaApi.on("select", update);
    emblaApi.on("reInit", update);
    return () => {
      emblaApi.off("select", update);
      emblaApi.off("reInit", update);
    };
  }, [emblaApi]);

  // Re-init on resize/orientation change so slide widths recalc cleanly
  // (prevents drag from "jumping" between breakpoints).
  useEffect(() => {
    if (!emblaApi) return;
    let raf = 0;
    const onResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => emblaApi.reInit());
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, [emblaApi]);

  const handlePrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const handleNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollToIndex = useCallback((i: number) => emblaApi?.scrollTo(i), [emblaApi]);

  if (cars.length === 0) {
    return (
      <div className="mt-10 text-center text-muted-foreground py-12">
        {pickLang(STRINGS.catalog.emptyCategory, lang)}
      </div>
    );
  }

  return (
    <div className="mt-10 relative">
      {/* Padding so highlight glow + hover shadow are not clipped by overflow */}
      <div className="overflow-hidden -mx-6 -my-12 px-6 py-12 sm:-mx-10 sm:px-10 lg:-mx-14 lg:px-14" ref={emblaRef}>
        <div
          className="flex"
          style={{
            // GPU compositing hints — smoother drag on low-end devices
            willChange: "transform",
            transform: "translateZ(0)",
            backfaceVisibility: "hidden",
          }}
        >
          {cars.map((car) => (
            <div
              key={car.id}
              className="flex-[0_0_100%] sm:flex-[0_0_50%] lg:flex-[0_0_33.333%] min-w-0 px-2 sm:px-3"
            >
              <CarCard car={car} onOpen={onOpen} />
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 flex items-center justify-center gap-6">
        <button
          type="button"
          aria-label={lang === "en" ? "Previous" : "Назад"}
          onClick={handlePrev}
          className="h-10 w-10 md:h-11 md:w-11 rounded-full bg-primary text-primary-foreground shadow-[0_4px_20px_hsl(var(--primary)/0.5)] flex items-center justify-center hover:scale-110 hover:bg-primary/90 transition-all duration-300 cursor-pointer"
        >
          <ChevronLeft size={20} strokeWidth={2.5} />
        </button>

        <div className="text-sm text-black tabular-nums min-w-[3rem] text-center">
          <span className="font-semibold">{currentSlide + 1}</span>
          <span className="mx-1">/</span>
          <span>{cars.length}</span>
        </div>

        <button
          type="button"
          aria-label={lang === "en" ? "Next" : "Вперёд"}
          onClick={handleNext}
          className="h-10 w-10 md:h-11 md:w-11 rounded-full bg-primary text-primary-foreground shadow-[0_4px_20px_hsl(var(--primary)/0.5)] flex items-center justify-center hover:scale-110 hover:bg-primary/90 transition-all duration-300 cursor-pointer"
        >
          <ChevronRight size={20} strokeWidth={2.5} />
        </button>
      </div>

      {/* Dots indicator — synced with embla (including loop) */}
      <div className="mt-4 flex items-center justify-center gap-2" role="tablist" aria-label={lang === "en" ? "Slide indicator" : "Индикатор слайдов"}>
        {cars.map((_, i) => (
          <Dot
            key={i}
            index={i}
            active={i === currentSlide}
            lang={lang}
            onSelect={scrollToIndex}
          />
        ))}
      </div>
    </div>
  );
};

const Dot = memo(function Dot({
  index,
  active,
  lang,
  onSelect,
}: {
  index: number;
  active: boolean;
  lang: string;
  onSelect: (i: number) => void;
}) {
  const handleClick = useCallback(() => onSelect(index), [index, onSelect]);
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      aria-label={lang === "en" ? `Go to slide ${index + 1}` : `Перейти к слайду ${index + 1}`}
      onClick={handleClick}
      className="inline-flex items-center justify-center h-6 w-6 -m-2 first:ml-0 last:mr-0"
    >
      <span
        aria-hidden="true"
        className={`block h-2 rounded-full transition-all duration-300 ${
          active ? "w-6 bg-primary" : "w-2 bg-foreground/25 hover:bg-foreground/40"
        }`}
      />
    </button>
  );
});

const CarCard = memo(function CarCard({ car, onOpen }: { car: CarRow; onOpen: (car: CarRow) => void }) {
  const { lang } = useLang();
  const name = pickText(car.name, car.name_en, lang);
  const priceLabel = pickShortLabel(car.price_label, car.price_label_en, lang);
  const fallbackPrice = `${pickLang(STRINGS.catalog.pricePerDayPrefix, lang)} ${car.price_long}${pickLang(STRINGS.catalog.pricePerDaySuffix, lang)}`;
  const handleOpen = useCallback(() => onOpen(car), [car, onOpen]);
  return (
    <article data-car-id={car.id} data-car-name={car.name} className="car-card group relative rounded-3xl overflow-hidden bg-card aspect-square card-hover">
      <img
        src={car.image_url || carPlaceholder}
        alt={getI18n(lang).carAlt(name)}
        loading="lazy"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition duration-500"
      />
      <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6 flex items-end justify-between gap-4 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
        <div className="text-white min-w-0">
          <h3 className="font-semibold text-xl sm:text-2xl truncate">{name}</h3>
          <p className="text-white/80 text-sm sm:text-base mt-1">
            {priceLabel || fallbackPrice}
          </p>
        </div>
        <button
          type="button"
          onClick={handleOpen}
          className="shrink-0 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-5 py-3 text-sm sm:text-base shadow-[0_4px_20px_hsl(var(--primary)/0.4)] transition"
        >
          {pickLang(STRINGS.catalog.moreBtn, lang)}
        </button>
      </div>
    </article>
  );
});

const CarModal = ({ car, onClose, bookingUrl }: { car: CarRow | null; onClose: () => void; bookingUrl: string }) => {
  const { lang } = useLang();
  const gallery = useMemo(() => {
    if (!car) return [] as string[];
    const cover = car.image_url ?? "";
    // Exclude the cover image from the in-modal gallery so users only see
    // the additional photos (cover is already shown on the card).
    const arr = ((car.gallery ?? []).filter(Boolean) as string[]).filter(
      (src) => src !== cover,
    );
    if (arr.length === 0) arr.push(cover || carPlaceholder);
    return arr;
  }, [car]);
  const [activeIdx, setActiveIdx] = useState(0);
  useEffect(() => setActiveIdx(0), [car?.id]);

  // Prefetch a gallery image into browser cache (no-op if already cached)
  const prefetchImage = useCallback((src: string) => {
    if (!src) return;
    const img = new Image();
    img.decoding = "async";
    img.src = src;
  }, []);

  // After modal opens & first image is prioritised, prefetch the next slide
  // during idle time so forward navigation feels instant without blocking LCP.
  useEffect(() => {
    if (!car || gallery.length < 2) return;
    const next = gallery[(activeIdx + 1) % gallery.length];
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    const schedule = w.requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 600));
    const id = schedule(() => prefetchImage(next), { timeout: 1500 });
    return () => {
      if (typeof id === "number") clearTimeout(id);
    };
  }, [car?.id, activeIdx, gallery, prefetchImage]);

  const name = car ? pickText(car.name, car.name_en, lang) : "";
  const description = car ? pickText(car.description, car.description_en, lang) : "";
  const photoLabel = pickLang(STRINGS.catalog.photoLabel, lang);
  const priceLines = car
    ? [
        { p: car.price_short, l: pickLang(STRINGS.catalog.days_1_5, lang) },
        { p: car.price_mid, l: pickLang(STRINGS.catalog.days_5_10, lang) },
        { p: car.price_long, l: pickLang(STRINGS.catalog.days_10_plus, lang) },
      ]
    : [];


  return (
    <Dialog open={!!car} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl w-[calc(100vw-1rem)] sm:w-full p-0 bg-card overflow-hidden !rounded-3xl max-h-[92vh] overflow-y-auto">
        {car && (
          <>
            {/* Mobile layout */}
            <div className="md:hidden flex flex-col">
              <div className="relative aspect-[4/3] w-full">
                <img
                  src={gallery[activeIdx]}
                  alt={name}
                  fetchPriority="high"
                  loading="eager"
                  decoding="async"
                  width={1200}
                  height={900}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
              {gallery.length > 1 && (
                <div className="flex gap-2 p-3 overflow-x-auto overflow-y-visible flex-nowrap">
                  {gallery.map((src, i) =>
                    i === activeIdx ? null : (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setActiveIdx(i)}
                        onMouseEnter={() => prefetchImage(src)}
                        onTouchStart={() => prefetchImage(src)}
                        aria-label={`${photoLabel} ${i + 1}`}
                        className="w-20 h-14 rounded-xl overflow-hidden bg-muted shrink-0 transition"
                      >
                        <img src={src} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                      </button>
                    ),
                  )}
                </div>
              )}
              <div className="px-5 pb-5">
                <h3 className="text-2xl font-bold tracking-tight leading-tight">{name}</h3>
                <p className="mt-2 text-foreground/80 leading-relaxed text-sm">{description}</p>

                <div className="mt-5 rounded-2xl bg-white/15 backdrop-blur-md border border-white/25 text-white px-2 py-3 grid grid-cols-3 divide-x divide-white/25 shadow-[0_8px_32px_rgba(0,0,0,0.25)]">
                  {priceLines.map((it) => (
                    <div key={it.l} className="px-2 text-center">
                      <div className="text-lg font-bold leading-tight">{it.p}</div>
                      <div className="text-[10px] opacity-70 mt-0.5">{it.l}</div>
                    </div>
                  ))}
                </div>

                <div className="mt-5 flex gap-3">
                  <Button
                    variant="outline"
                    onClick={onClose}
                    className="flex-1 rounded-full h-11 text-primary bg-transparent hover:bg-primary hover:text-primary-foreground"
                  >
                    {pickLang(STRINGS.catalog.backBtn, lang)}
                  </Button>
                  <a href={bookingUrl} target="_blank" rel="noopener noreferrer" className="flex-1">
                    <Button className="w-full rounded-full h-11">{pickLang(STRINGS.catalog.bookBtn, lang)}</Button>
                  </a>
                </div>
              </div>
            </div>

            {/* Desktop layout */}
            <div className="hidden md:block relative aspect-[1.75/1] w-full">
              <img
                src={gallery[activeIdx]}
                alt={name}
                fetchPriority="high"
                loading="eager"
                decoding="async"
                width={1600}
                height={914}
                className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-card/95 via-card/40 to-card/20" />

              <div className="absolute top-12 left-12 right-12 z-10 flex items-start justify-between gap-6">
                <div className="max-w-md">
                  <h3 className="text-5xl font-bold tracking-tight leading-tight">{name}</h3>
                  <p className="mt-4 text-foreground/80 leading-relaxed text-base">{description}</p>
                </div>
                {gallery.length > 1 && (
                  <div className="flex gap-3 shrink-0 flex-nowrap overflow-x-auto overflow-y-visible max-w-[60%] justify-end p-1 -m-1">
                    {gallery.map((src, i) =>
                      i === activeIdx ? null : (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setActiveIdx(i)}
                          onMouseEnter={() => prefetchImage(src)}
                          onTouchStart={() => prefetchImage(src)}
                          aria-label={`${photoLabel} ${i + 1}`}
                          className="w-24 h-16 rounded-2xl overflow-hidden bg-muted transition shrink-0 hover:ring-2 hover:ring-primary"
                        >
                          <img src={src} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                        </button>
                      ),
                    )}
                  </div>
                )}
              </div>

              <div className="absolute bottom-10 left-10 z-10">
                <div className="rounded-2xl bg-white/15 backdrop-blur-md border border-white/25 text-white px-8 py-4 grid grid-cols-3 divide-x divide-white/25 shadow-[0_8px_32px_rgba(0,0,0,0.25)]">
                  {priceLines.map((it) => (
                    <div key={it.l} className="px-6 text-center">
                      <div className="text-3xl font-bold leading-tight">{it.p}</div>
                      <div className="text-sm opacity-70 mt-0.5">{it.l}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="absolute bottom-10 right-10 z-10 flex gap-3">
                <Button
                  variant="outline"
                  onClick={onClose}
                  className="rounded-full h-12 px-8 text-primary bg-transparent hover:bg-primary hover:text-primary-foreground"
                >
                  {pickLang(STRINGS.catalog.backBtn, lang)}
                </Button>
                <a href={bookingUrl} target="_blank" rel="noopener noreferrer">
                  <Button className="rounded-full h-12 px-8">{pickLang(STRINGS.catalog.bookBtn, lang)}</Button>
                </a>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Index = () => {
  const { settings, cars } = useSiteContent();
  const { lang } = useLang();
  const [activeCat, setActiveCat] = useState<CategoryKey>("econom");
  const [openCar, setOpenCar] = useState<CarRow | null>(null);
  const [highlightedNames, setHighlightedNames] = useState<string[]>([]);
  const visibleCars = cars.filter((c) => c.category === activeCat);

  const handleSeoCtaClick = useCallback(
    (cat: CategoryKey, nameMatch: string[], ctaId: string, ctaLabel: string) => {
      // Analytics: send event to GA if available
      try {
        const w = window as unknown as { gtag?: (...args: unknown[]) => void; dataLayer?: unknown[] };
        if (typeof w.gtag === "function") {
          w.gtag("event", "seo_card_cta_click", {
            event_category: "seo_cards",
            event_label: ctaLabel,
            cta_id: ctaId,
            target_category: cat,
          });
        } else if (Array.isArray(w.dataLayer)) {
          w.dataLayer.push({
            event: "seo_card_cta_click",
            cta_id: ctaId,
            cta_label: ctaLabel,
            target_category: cat,
          });
        }
      } catch {
        // noop
      }

      // Switch catalog filter
      setActiveCat(cat);
      setHighlightedNames(nameMatch);

      // Smooth, eased scroll to catalog (longer & smoother than default)
      requestAnimationFrame(() => {
        const el = document.getElementById("catalog");
        if (!el) return;
        const startY = window.scrollY;
        const targetY = el.getBoundingClientRect().top + window.scrollY - 20;
        const distance = targetY - startY;
        const duration = Math.min(1400, Math.max(700, Math.abs(distance) * 0.7));
        const startTime = performance.now();
        const easeInOutCubic = (t: number) =>
          t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
        const step = (now: number) => {
          const elapsed = now - startTime;
          const t = Math.min(1, elapsed / duration);
          window.scrollTo(0, startY + distance * easeInOutCubic(t));
          if (t < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      });

      // Clear highlight after 3.5s (a bit longer so it stays after scroll completes)
      window.setTimeout(() => setHighlightedNames([]), 3500);
    },
    [],
  );

  // Apply highlight/dim classes to car cards in DOM after render & on highlight change
  useEffect(() => {
    const cards = document.querySelectorAll<HTMLElement>(".car-card");
    cards.forEach((card) => {
      const name = card.getAttribute("data-car-name") ?? "";
      if (highlightedNames.length === 0) {
        card.classList.remove("car-card--highlight", "car-card--dim");
        return;
      }
      if (highlightedNames.includes(name)) {
        card.classList.add("car-card--highlight");
        card.classList.remove("car-card--dim");
      } else {
        card.classList.add("car-card--dim");
        card.classList.remove("car-card--highlight");
      }
    });
  }, [highlightedNames, activeCat, visibleCars.length]);

  // Localized labels for catalog categories
  const catLabel = (k: CategoryKey) => {
    const ru = settings.catalog.categoryLabels[k];
    const en = settings.catalog.categoryLabels_en?.[k];
    return pickText(ru, en, lang);
  };
  const cats: { key: CategoryKey; label: string }[] = [
    { key: "econom", label: catLabel("econom") },
    { key: "comfort", label: catLabel("comfort") },
    { key: "business", label: catLabel("business") },
    { key: "premium", label: catLabel("premium") },
  ];

  // Localized section content
  const pT = (ru?: string | null, en?: string | null) =>
    pickTextWithDict(ru, en, lang, DEFAULT_RU_EN_DICT);

  const t = getI18n(lang);
  const heroTitle = lang === "ru" ? pT(settings.hero.title, settings.hero.title_en) : t.hero.h1;
  const heroSubtitle = lang === "ru" ? pT(settings.hero.subtitle, settings.hero.subtitle_en) : t.hero.subtitle;
  const heroCta = lang === "ru" ? pT(settings.hero.ctaLabel, settings.hero.ctaLabel_en) : t.hero.cta;
  const heroFeatures = settings.hero.features.map((f, i) =>
    pT(f, settings.hero.features_en?.[i]),
  );

  const aboutEyebrow = pT(settings.about.eyebrow, settings.about.eyebrow_en);
  const aboutTitle = pT(settings.about.title, settings.about.title_en);
  const aboutP1 = pT(settings.about.paragraph1, settings.about.paragraph1_en);
  const aboutP2 = pT(settings.about.paragraph2, settings.about.paragraph2_en);

  const catalogTitle = pT(settings.catalog.title, settings.catalog.title_en);
  const catalogDesc = pT(settings.catalog.description, settings.catalog.description_en);

  const processTitle = pT(settings.process.title, settings.process.title_en);
  const processSteps = settings.process.steps.map((s, i) =>
    pT(s, settings.process.steps_en?.[i]),
  );

  const consultTitle = pT(settings.consult.title, settings.consult.title_en);
  const consultDesc = pT(settings.consult.description, settings.consult.description_en);
  const consultSubmit = pT(settings.consult.submitLabel, settings.consult.submitLabel_en);
  const consultName = pT(settings.consult.namePlaceholder, settings.consult.namePlaceholder_en);
  const consultPhone = pT(settings.consult.phonePlaceholder, settings.consult.phonePlaceholder_en);

  const faqTitle = pT(settings.faq.title, settings.faq.title_en);
  const faqDesc = pT(settings.faq.description, settings.faq.description_en);

  const footerCopyright = pT(settings.footer.copyright, settings.footer.copyright_en);

  // Деликатное появление при скролле для всех элементов с классом .reveal
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") {
      document.querySelectorAll(".reveal").forEach((el) => el.classList.add("is-visible"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" },
    );
    document.querySelectorAll(".reveal").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [activeCat, cars.length, lang]);

  return (
    <div
      className="min-h-screen bg-background text-foreground"
      style={{ fontFamily: settings.theme.fontBody }}
    >
      <Header />
      <ScrollToTop />
      <Suspense fallback={null}><ChatConsultant /></Suspense>
      <main>
      {/* HERO */}
      <section className="relative md:min-h-screen flex flex-col overflow-hidden" aria-label={lang === "en" ? "Hero" : "Главная"}>
        {/* Mobile: image on top in normal flow */}
        <img
          src={settings.hero.imageUrl ?? heroCarFallback}
          alt={t.seo.title}
          fetchPriority="high"
          decoding="async"
          width={1600}
          height={900}
          className="md:hidden w-full h-[55vh] min-h-[320px] max-h-[520px] object-cover"
        />

        {/* Desktop: full-bleed background image */}
        <img
          src={settings.hero.imageUrl ?? heroCarFallback}
          alt={t.seo.title}
          decoding="async"
          fetchPriority="high"
          width={1920}
          height={1080}
          className="hidden md:block absolute inset-0 w-full h-full object-cover z-0"
        />
        {/* Desktop legibility gradient */}
        <div
          aria-hidden
          className="hidden md:block absolute inset-0 z-10 bg-gradient-to-r from-black/55 via-black/15 to-transparent pointer-events-none"
        />

        {/* Content */}
        <div className="relative z-20 flex-1 flex flex-col site-container w-full pt-6 pb-8 md:pt-28 md:pb-10">
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <h1
              className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight max-w-3xl text-balance text-foreground md:text-white"
              style={{ fontFamily: settings.theme.fontHeading }}
            >
              {heroTitle}
            </h1>
            <p className="mt-4 sm:mt-6 text-base sm:text-lg text-foreground/80 md:text-white max-w-md text-pretty">
              {heroSubtitle}
            </p>
            <Button
              asChild
              className="mt-6 sm:mt-8 rounded-full h-11 sm:h-12 px-6 sm:px-8 text-sm sm:text-base self-start"
            >
              <a href="https://wa.me/996508810010" target="_blank" rel="noopener noreferrer">
                {heroCta}
              </a>
            </Button>
          </div>

          {/* Desktop features card — in normal flow, pinned to bottom by parent flex */}
          <div className="hidden md:block mt-10">
            <div className="rounded-full grid grid-cols-4 bg-card shadow-[0_8px_40px_rgba(0,0,0,0.45)]">
              {heroFeatures.map((t, i) => (
                <div
                  key={i}
                  className="relative px-6 lg:px-8 py-6 flex items-center justify-center text-center text-sm lg:text-base text-foreground whitespace-pre-line before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-10 before:w-px before:bg-border first:before:hidden"
                >
                  {t}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mobile features (under hero) */}
      <section className="md:hidden bg-background pt-6 pb-10">
        <div className="site-container">
          <div className="rounded-3xl grid grid-cols-2 bg-card shadow-[0_8px_40px_rgba(0,0,0,0.45)] divide-x divide-border overflow-hidden">
            {heroFeatures.map((t, i) => (
              <div
                key={i}
                className={`px-3 py-6 min-h-[96px] flex items-center justify-center text-center text-xs leading-snug whitespace-pre-line text-foreground ${i >= 2 ? "border-t border-border" : ""}`}
              >
                {t}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LIGHT SECTION WRAPPER: About → FAQ */}
      <div className="bg-white text-[hsl(0_0%_10%)] [--muted-foreground:0_0%_35%] [--border:0_0%_88%] [--card:0_0%_98%] [--secondary:0_0%_94%] [--accent:0_0%_94%]">
        {/* ABOUT */}
        <section id="aboutus" className="py-12 md:py-24">
          <div className="site-container grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-primary font-semibold tracking-widest text-sm">{aboutEyebrow}</div>
              <h2
                className="reveal mt-4 text-3xl md:text-5xl font-bold tracking-tight"
                style={{ fontFamily: settings.theme.fontHeading }}
              >
                {aboutTitle}
              </h2>
              <p className="mt-6 text-muted-foreground leading-relaxed">{aboutP1}</p>
              <p className="mt-4 text-muted-foreground leading-relaxed">{aboutP2}</p>
            </div>
            <div className="rounded-3xl overflow-hidden">
              <img
                src={settings.about.imageUrl ?? (lang === "en" ? aboutFallbackEn : aboutFallback)}
                alt={lang === "en" ? "Freeways car rental in Bishkek" : "Аренда авто Freeways в Бишкеке"}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* CONSULTATION 1 */}
        <section className="py-6 md:py-12">
          <div className="site-container">
            <ConsultForm
              title={consultTitle}
              description={consultDesc}
              bannerUrl={settings.consult.imageUrl}
              submitLabel={consultSubmit}
              namePlaceholder={consultName}
              phonePlaceholder={consultPhone}
              buttonColor={settings.consult.buttonColor}
            />
          </div>
        </section>

        {/* SEO CONTENT SECTIONS — visible, indexable, single H1 preserved (H2 used here) */}
        {/* TODO i18n: localize to ky/en/uk/be/fr/zh/tr/ko/de/hi/ar/ja/es. RU shown as primary. */}
        <section className="py-8 md:py-24" aria-label="Услуги аренды авто в Бишкеке">
          <div className="site-container">
            <h2
              className="reveal text-2xl md:text-5xl font-bold tracking-tight text-center"
              style={{ fontFamily: settings.theme.fontHeading }}
            >
              Аренда авто в Бишкеке и по Кыргызстану
            </h2>
            <p className="reveal mt-3 md:mt-4 text-sm md:text-base text-center text-muted-foreground max-w-2xl mx-auto">
              Подберём автомобиль под любую задачу — от поездок по городу до путешествий по горным маршрутам Кыргызстана.
            </p>
            {(() => {
              const items = [
                {
                  h: "Прокат авто в Бишкеке без водителя",
                  p: "Аренда авто без водителя Бишкек на сутки и на длительный срок: эконом, комфорт и бизнес-класс. Прокат авто без водителя Бишкек с прозрачным договором, страховкой и быстрой подачей машины в любую точку города.",
                  img: seoSedan,
                  cta: "Смотреть эконом и комфорт авто",
                  cat: "econom" as CategoryKey,
                  nameMatch: ["Hyundai Solaris", "Hyundai Accent", "Kia K5 2018"],
                  ctaId: "econom",
                },
                {
                  h: "Аренда внедорожников и SUV в Бишкеке",
                  p: "Аренда внедорожника Бишкек и аренда SUV Бишкек для горных дорог, трасс и бездорожья. Прокат джипа Бишкек подойдёт для маршрутов на Ала-Арчу, Чункурчак, Сон-Куль и в любые регионы Кыргызстана.",
                  img: seoSuv,
                  cta: "Смотреть внедорожники и SUV",
                  cat: "comfort" as CategoryKey,
                  nameMatch: ["Toyota 4Runner", "Kia Sorento", "Toyota Highlander", "Kia Carnival"],
                  ctaId: "suv",
                },
                {
                  h: "Трансфер из аэропорта Манас",
                  p: "Трансфер аэропорт Манас 24/7: встреча с табличкой, доставка в Бишкек, на Иссык-Куль и в другие города. Также доступна аренда авто аэропорт Манас — заберите машину сразу по прилёту.",
                  img: seoAirport,
                  cta: "Выбрать авто для трансфера",
                  cat: "comfort" as CategoryKey,
                  nameMatch: ["Toyota Camry 75", "Kia K5 2020", "Kia Carnival"],
                  ctaId: "transfer",
                },
                {
                  h: "Премиум авто в аренду: BMW, Mercedes, Lexus",
                  p: "Премиум авто Бишкек для деловых встреч, свадеб и особых событий. Аренда BMW Бишкек, аренда Mercedes Бишкек и аренда Lexus Бишкек — с водителем или без, с подачей в удобное место.",
                  img: seoPremium,
                  cta: "Смотреть премиум авто",
                  cat: "premium" as CategoryKey,
                  nameMatch: ["Mercedes W222", "Mercedes GLS", "BMW X7", "BMW X6", "BMW X5 F15"],
                  ctaId: "premium",
                },
                {
                  h: "Поездки на Иссык-Куль, в Каракол и по Кыргызстану",
                  p: "Аренда авто Иссык-Куль, поездки в Чолпон-Ату, Каракол, Ош и Талас. Аренда авто Кыргызстан и прокат авто Кыргызстан для индивидуальных маршрутов и джип-туров по всей стране.",
                  img: seoTravel,
                  cta: "Подобрать авто для поездки",
                  cat: "business" as CategoryKey,
                  nameMatch: ["Range Rover", "Lexus LX 570", "Lexus GX", "BMW X5 G05"],
                  ctaId: "travel",
                },
              ];
              const rows = [items.slice(0, 3), items.slice(3, 5)];
              const renderCard = (s: typeof items[number], i: number) => (
                <article
                  key={`${i}-${s.ctaId}`}
                  tabIndex={0}
                  className="seo-card group reveal relative overflow-hidden rounded-3xl border border-border bg-[hsl(0_0%_6%)] p-4 md:p-7 h-auto md:h-[340px] lg:h-[340px] shadow-lg transition-[box-shadow,border-color,flex-grow,flex-basis] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] hover:shadow-2xl hover:border-primary/40 focus-visible:shadow-2xl focus-visible:border-primary/60 focus-visible:outline-none lg:basis-0 lg:grow lg:hover:grow-[3] lg:focus-within:grow-[3] lg:min-w-0"
                >
                  <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-3xl" aria-hidden="true">
                    <img
                      src={s.img}
                      alt=""
                      loading="lazy"
                      width={768}
                      height={768}
                      className="select-none absolute -bottom-3 -right-3 md:-bottom-6 md:-right-6 w-20 sm:w-28 md:w-48 lg:w-56 h-auto object-contain opacity-60 md:opacity-100 transition-[transform,opacity] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-x-6 group-hover:opacity-40 group-focus-visible:translate-x-6 group-focus-visible:opacity-40 drop-shadow-2xl"
                    />
                  </div>
                  <div className="relative z-10 flex flex-col h-full">
                    <h2
                      className="text-base sm:text-lg md:text-2xl font-semibold tracking-tight text-foreground line-clamp-2 md:line-clamp-none min-h-[2.75rem] sm:min-h-[3.25rem] md:min-h-0 transition-opacity duration-500 ease-out lg:group-hover/row:opacity-0 lg:group-focus-within/row:opacity-0 lg:group-hover:!opacity-100 lg:group-focus-within:!opacity-100"
                      style={{ fontFamily: settings.theme.fontHeading }}
                    >
                      {s.h}
                    </h2>
                    <p
                      className="seo-card-desc mt-2 md:mt-3 text-[hsl(0_0%_85%)] leading-snug md:leading-relaxed text-[12px] sm:text-[13px] md:text-base line-clamp-3 md:line-clamp-none md:h-auto overflow-hidden opacity-0 translate-y-1 lg:max-h-0 group-hover:opacity-100 group-hover:translate-y-0 lg:group-hover:max-h-[600px] group-focus-visible:opacity-100 group-focus-visible:translate-y-0 lg:group-focus-visible:max-h-[600px]"
                    >
                      {s.p}
                    </p>
                    <div className="seo-card-cta mt-auto pt-4 md:pt-5 min-h-[2.75rem] sm:min-h-[3rem] flex items-end opacity-100 translate-y-0 md:opacity-0 md:translate-y-2 md:pointer-events-none md:group-hover:opacity-100 md:group-hover:translate-y-0 md:group-hover:pointer-events-auto md:group-focus-visible:opacity-100 md:group-focus-visible:translate-y-0 md:group-focus-visible:pointer-events-auto">
                      <button
                        type="button"
                        onClick={() => handleSeoCtaClick(s.cat, s.nameMatch, s.ctaId, s.cta)}
                        className="inline-flex items-center justify-center gap-1.5 sm:gap-2 rounded-full bg-white text-[hsl(0_0%_8%)] hover:bg-primary hover:text-primary-foreground px-3.5 sm:px-5 py-2 sm:py-2.5 text-[12px] sm:text-sm font-semibold shadow-[0_4px_20px_rgba(255,255,255,0.15)] hover:shadow-[0_4px_20px_hsl(var(--primary)/0.5)] transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-[hsl(0_0%_6%)] max-w-full"
                      >
                        <span className="truncate">{s.cta}</span>
                        <ChevronRight size={14} strokeWidth={2.5} className="shrink-0" />
                      </button>
                    </div>
                  </div>
                </article>
              );
              return (
                 <SeoCardsMobileLoop items={items} renderCard={renderCard} desktop={
                  <div className="hidden lg:flex lg:flex-col lg:gap-5">
                    {rows.map((row, ri) => (
                      <div
                        key={ri}
                        className="group/row flex flex-row items-stretch gap-5"
                      >
                        {row.map((s, i) => renderCard(s, ri * 10 + i))}
                      </div>
                    ))}
                  </div>
                } />
              );
            })()}
          </div>
        </section>

        {/* SERVICES + ADVANTAGES (SEO) — hidden visually, kept for SEO */}
        <section aria-hidden="true" className="sr-only">
          <div className="site-container grid lg:grid-cols-2 gap-10">
            <div>
              <h2 className="text-2xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: settings.theme.fontHeading }}>
                {t.services.title}
              </h2>
              <ul className="mt-6 grid sm:grid-cols-2 gap-3">
                {t.services.items.map((s, i) => (
                  <li key={i} className="rounded-2xl bg-[hsl(0_0%_98%)] border border-[hsl(0_0%_88%)] p-4">
                    <h3 className="font-semibold text-base">{s.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{s.desc}</p>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="text-2xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: settings.theme.fontHeading }}>
                {t.advantages.title}
              </h2>
              <ul className="mt-6 space-y-2">
                {t.advantages.items.map((a, i) => (
                  <li key={i} className="rounded-xl bg-[hsl(0_0%_98%)] border border-[hsl(0_0%_88%)] px-4 py-3 text-sm">
                    {a}
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-xs text-muted-foreground leading-relaxed">{t.servicesKeywords}</p>
              {t.longSeo && (
                <p className="mt-3 text-xs text-muted-foreground leading-relaxed">{t.longSeo}</p>
              )}
            </div>
          </div>
        </section>

        {/* CATALOG */}
        <section id="catalog" className="py-12 md:py-24 overflow-x-hidden">
          <div className="site-container">
            <h2
              className="reveal text-3xl md:text-5xl font-bold tracking-tight text-center"
              style={{ fontFamily: settings.theme.fontHeading }}
            >
              {catalogTitle}
            </h2>
            <p className="mt-4 text-center text-muted-foreground max-w-2xl mx-auto">
              {catalogDesc}
            </p>

            <div className="mt-6 md:mt-10 flex flex-col items-center gap-3">
              <div
                className="grid grid-cols-2 md:flex md:flex-wrap md:justify-center gap-2 p-2 rounded-[15px] md:rounded-full bg-white w-full max-w-xs md:max-w-none md:w-fit mx-auto shadow-[0_8px_32px_rgba(0,0,0,0.12)]"
              >
                {cats.map((c) => (
                  <button
                    key={c.key}
                    onClick={() => setActiveCat(c.key)}
                    className={`px-5 py-2 rounded-[10px] md:rounded-full text-sm font-medium transition ${
                      activeCat === c.key
                        ? "bg-primary text-primary-foreground shadow-[0_4px_16px_hsl(var(--primary)/0.4)]"
                        : "text-[hsl(0_0%_15%)] hover:bg-secondary"
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setHighlightedNames([])}
                aria-hidden={highlightedNames.length === 0}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs md:text-sm font-medium border border-primary/30 bg-primary/10 text-primary hover:bg-primary/20 transition-all duration-300 ${
                  highlightedNames.length > 0
                    ? "opacity-100 translate-y-0 pointer-events-auto"
                    : "opacity-0 -translate-y-1 pointer-events-none"
                }`}
              >
                <span aria-hidden="true">×</span>
                {pT("Показать все", "Show all")}
              </button>
            </div>

            <CarsCarousel cars={visibleCars} onOpen={setOpenCar} />
          </div>
        </section>

        <CarModal car={openCar} onClose={() => setOpenCar(null)} bookingUrl={settings.consult.bookingUrl} />

        {/* PROCESS */}
        <section className="pt-12 md:pt-24 pb-6 md:pb-12">
          <div className="site-container">
            {/* MOBILE: header → steps card → image (stacked, общая стилистика) */}
            <div className="md:hidden">
              <h2
                className="reveal text-2xl font-bold tracking-tight text-[hsl(0_0%_10%)]"
                style={{ fontFamily: settings.theme.fontHeading }}
              >
                {processTitle}
              </h2>

              <div className="mt-6 rounded-[20px] bg-white shadow-[0_8px_40px_rgba(0,0,0,0.12)] grid grid-cols-2 overflow-hidden">
                {processSteps.map((step, i) => (
                  <div
                    key={i}
                    className={`px-4 py-5 min-h-[96px] flex items-center justify-center text-center text-sm leading-snug whitespace-pre-line text-[hsl(0_0%_15%)] ${
                      i % 2 === 1 ? "border-l border-[hsl(0_0%_90%)]" : ""
                    } ${i >= 2 ? "border-t border-[hsl(0_0%_90%)]" : ""}`}
                  >
                    {step}
                  </div>
                ))}
              </div>

              <div className="mt-6 rounded-[20px] overflow-hidden aspect-[4/3]">
                <img
                  src={settings.process.imageUrl ?? processFallback}
                  alt={lang === "en" ? "Car rental process" : "Процесс аренды автомобиля"}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* DESKTOP: original overlay layout */}
            <div className="hidden md:block relative rounded-[20px] overflow-hidden min-h-[640px]">
              <img
                src={settings.process.imageUrl ?? processFallback}
                alt={lang === "en" ? "Car rental process" : "Процесс аренды автомобиля"}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover"
              />

              <h2
                className="reveal relative z-10 p-12 text-5xl font-bold tracking-tight text-white"
                style={{ fontFamily: settings.theme.fontHeading }}
              >
                {processTitle}
              </h2>

              <div className="absolute bottom-8 left-8 right-8 z-10">
                <div className="rounded-[20px] grid grid-cols-4 bg-card shadow-[0_8px_40px_rgba(0,0,0,0.45)]">
                  {processSteps.map((step, i) => (
                    <div
                      key={i}
                      className="relative px-6 py-6 flex items-center justify-center text-center text-base whitespace-pre-line text-muted before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-10 before:w-px before:bg-border first:before:hidden"
                    >
                      {step}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CONSULTATION 2 */}
        <section className="pt-0 pb-6 md:pb-12">
          <div className="site-container">
            <ConsultForm
              title={consultTitle}
              description={consultDesc}
              bannerUrl={settings.consult.imageUrl2}
              submitLabel={consultSubmit}
              namePlaceholder={consultName}
              phonePlaceholder={consultPhone}
              buttonColor={settings.consult.buttonColor}
            />
          </div>
        </section>

        {/* "Как ещё ищут" — natural variants & misspellings block (single, low-emphasis) */}
        <section className="sr-only" aria-label="Как ещё ищут аренду авто в Бишкеке">
          <div>
            <h2>Как ещё ищут аренду авто в Бишкеке</h2>
            <p>
              Пользователи также ищут нас по запросам: аренда авто бишкек, аренда авто в бишкеке, прокат авто бишкек, прокат машин бишкек, машина на прокат бишкек, машына напракат бишкек, аренда машын бишкек, аренда машин бишкек, арнда авто бишкек, аренда авто бишке, авто на прокат бишкек, машина в аренду бишкек, джип на прокат бишкек, аренда джипа бишкек, трансфер манас, такси манас бишкек, аренда авто манас, аренда авто иссык куль, машина на иссык куль, прокат авто кыргызстан, аренда авто кыргызстан.
            </p>
          </div>
        </section>

      </div>
      {/* /LIGHT SECTION WRAPPER */}


      {/* FAQ — dark */}
      <section className="py-12 md:py-24 bg-[hsl(0_0%_4%)] text-white">
        <div className="site-container max-w-[1200px]">
          <h2
            className="reveal text-3xl md:text-5xl font-medium tracking-tight text-center text-white"
            style={{ fontFamily: settings.theme.fontHeading }}
          >
            {lang === "ru" ? faqTitle : t.faq.title}
          </h2>
          <p className="mt-4 text-center text-[hsl(0_0%_55%)]">{lang === "ru" ? faqDesc : t.faq.description}</p>
          <Accordion
            type="single"
            collapsible
            className="mt-12 grid md:grid-cols-2 gap-4"
          >
            {(lang === "ru"
              ? settings.faq.items.map((it) => ({ q: pT(it.q, it.q_en), a: pT(it.a, it.a_en) }))
              : t.faq.items
            ).map((item, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="border-0 bg-[hsl(0_0%_93%)] rounded-[10px] px-5 text-[hsl(0_0%_15%)] h-fit"
              >
                <AccordionTrigger className="text-center text-[15px] font-medium hover:no-underline justify-center [&>svg]:text-[hsl(0_0%_60%)]">
                  <span className="flex-1 text-center">{item.q}</span>
                </AccordionTrigger>
                <AccordionContent className="text-[hsl(0_0%_45%)] text-[15px] leading-relaxed text-center">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
      </main>

      {/* FOOTER */}
      <footer id="contacts" className="bg-[hsl(0_0%_4%)] text-white py-12 md:py-16">
        <div className="site-container">
          {(() => {
            const visible = settings.footer.contacts.filter(
              (c) => c.type !== "address" && c.type !== "website",
            );
            const phones = visible.filter((c) => c.type === "phone");
            const others = visible.filter((c) => c.type !== "phone");
            const cards: Array<
              | { kind: "single"; contact: typeof visible[number] }
              | { kind: "phones"; items: typeof phones }
            > = [
              ...others.map((c) => ({ kind: "single" as const, contact: c })),
              ...(phones.length ? [{ kind: "phones" as const, items: phones }] : []),
            ];
            const iconFor = (t: string) =>
              t === "instagram" ? Instagram :
              t === "whatsapp" ? MessageCircle :
              t === "phone" ? Phone :
              t === "email" ? Mail :
              Globe;
            return (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {cards.map((card, i) => {
                  if (card.kind === "phones") {
                    return (
                      <div
                        key={`phones-${i}`}
                        className="bg-[hsl(0_0%_10%)] rounded-2xl px-6 py-7 flex flex-col items-center text-center gap-3 h-full"
                      >
                        <Phone size={28} className="text-red-500" strokeWidth={1.75} />
                        <div className="text-[hsl(0_0%_60%)] text-sm">
                          {pickLang(STRINGS.footer.phoneLabel, lang)}
                        </div>
                        <div className="flex flex-col gap-1">
                          {card.items.map((p, j) => (
                            <a
                              key={j}
                              href={p.url || `tel:${p.value.replace(/\s/g, "")}`}
                              className="text-white text-base font-medium hover:text-primary transition"
                            >
                              {pT(p.value, p.value_en)}
                            </a>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  const c = card.contact;
                  const Icon = iconFor(c.type);
                  const labelText = pT(c.label, c.label_en);
                  const valueText = pT(c.value, c.value_en);
                  const inner = (
                    <div className="bg-[hsl(0_0%_10%)] rounded-2xl px-6 py-7 flex flex-col items-center text-center gap-3 h-full transition hover:bg-[hsl(0_0%_14%)]">
                      <Icon size={28} className="text-red-500" strokeWidth={1.75} />
                      <div className="text-[hsl(0_0%_60%)] text-sm">{labelText}</div>
                      <div className="text-white text-base font-medium break-all">{valueText}</div>
                    </div>
                  );
                  return c.url ? (
                    <a key={i} href={c.url} target="_blank" rel="noreferrer" className="block">{inner}</a>
                  ) : (
                    <div key={i}>{inner}</div>
                  );
                })}
              </div>
            );
          })()}

          {/* Адрес + карта */}
          {(() => {
            const addressContact = settings.footer.contacts.find((c) => c.type === "address");
            const addressValue = addressContact
              ? pT(addressContact.value, addressContact.value_en)
              : (lang === "en" ? "Bishkek, Sultan Ibraimov street, 115/4" : "Бишкек, ул. Султана Ибраимова, 115/4");
            return (
              <div className="mt-6 grid gap-4 lg:grid-cols-[1fr_2fr] bg-[hsl(0_0%_10%)] rounded-2xl overflow-hidden">
                <div className="p-8 flex flex-col justify-center gap-3">
                  <MapPin size={28} className="text-red-500" strokeWidth={1.75} />
                  <div className="text-[hsl(0_0%_60%)] text-sm">{pickLang(STRINGS.footer.addressLabel, lang)}</div>
                  <div className="text-white text-xl font-medium">{addressValue}</div>
                  <a
                    href="https://go.2gis.com/3Q0kX"
                    target="_blank"
                    rel="noreferrer"
                    className="text-white text-sm hover:underline mt-1"
                  >
                    {pickLang(STRINGS.footer.openIn2gis, lang)}
                  </a>
                </div>
                <Suspense fallback={<div className="min-h-[260px] lg:min-h-[300px] bg-[hsl(0_0%_8%)]" />}>
                  <LazyMap title={pickLang(STRINGS.footer.mapTitle, lang)} />
                </Suspense>
              </div>
            );
          })()}

          <div className="mt-12 flex flex-col md:flex-row gap-6 items-center justify-between border-t border-[hsl(0_0%_15%)] pt-8">
            <img src={logo} alt="Freeways" loading="lazy" decoding="async" className="h-10 w-auto" />
            <div className="text-[hsl(0_0%_55%)] text-sm text-center space-y-1">
              <div>© {new Date().getFullYear()} {footerCopyright}</div>
              <div>
                © {lang === "ru" ? "Разработано" : "Developed by"}{" "}
                <a
                  href="https://curultai.com"
                  target="_blank"
                  rel="noreferrer"
                  className="text-white hover:underline"
                >
                  CurultAI Lab
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
